import {uuidv4} from "@/utils/helpers";
import {calculateHandwritingNoteTextMarginY} from "@/tools/GenogramTool/lib/GenogramHandwritingSpaceHelper";

/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
export const STICKY_NOTE_TYPE = "stickyNote"
export const STICKY_NOTE_DEFAULT_TEXT = ""
export const STICKY_NOTE_BACKGROUND_COLOR = "#F8F9FB"
export const STICKY_NOTE_BORDER_COLOR = "#D9DEE5"
export const STICKY_NOTE_BACKGROUND_OPACITY = 0.8
export const STICKY_NOTE_DEFAULT_WIDTH = 220
export const STICKY_NOTE_MIN_WIDTH = 140
export const STICKY_NOTE_MIN_HEIGHT = 90
export const STICKY_NOTE_TEXT_PADDING_X = 18
export const STICKY_NOTE_TEXT_PADDING_Y = 10
export const STICKY_NOTE_FONT_SIZE = 20
export const STICKY_NOTE_LINE_HEIGHT = 1
const STICKY_NOTE_FONT_FAMILY = "Arial, sans-serif"
const STICKY_NOTE_HEIGHT_SAFETY_PX = 4

let stickyNoteMeasureContext = null

export function isStickyNoteNodeData(nodeData) {
	return nodeData?.type === STICKY_NOTE_TYPE
}

export function syncStickyNoteNodeData(nodeData = {}) {
	if(!isStickyNoteNodeData(nodeData)){
		return nodeData
	}

	let noteText = typeof nodeData.noteText === "string" ? nodeData.noteText : ""
	let labelText = noteText || STICKY_NOTE_DEFAULT_TEXT
	let shouldUseFixedHeight = !!nodeData.keepFixedHeight
	let fixedHeight = Math.max(Number(nodeData.height) || 0, STICKY_NOTE_MIN_HEIGHT)
	let height = shouldUseFixedHeight ? fixedHeight : calculateStickyNoteHeight(labelText, nodeData.width, nodeData.extraHeight)
	let textMarginY = nodeData.isHandwritingSpaceNote ? calculateHandwritingNoteTextMarginY(labelText, height) : 0

	nodeData.noteText = noteText
	nodeData.label = labelText
	nodeData.width = Math.max(Number(nodeData.width) || STICKY_NOTE_DEFAULT_WIDTH, STICKY_NOTE_MIN_WIDTH)
	nodeData.height = height
	nodeData.shape = 'round-rectangle'
	nodeData.resize = true
	nodeData.bg = nodeData.bg || STICKY_NOTE_BACKGROUND_COLOR
	nodeData.borderColor = nodeData.borderColor || STICKY_NOTE_BORDER_COLOR
	nodeData.opacity = nodeData.opacity || STICKY_NOTE_BACKGROUND_OPACITY
	nodeData.border = true
	nodeData.showNotes = false
	nodeData.excludeFromLegend = true
	nodeData.textMarginY = textMarginY
	return nodeData
}

export function calculateStickyNoteHeight(text = "", width = STICKY_NOTE_DEFAULT_WIDTH, extraHeight = 0) {
	let textHeight = calculateStickyNoteTextHeight(text, width)
	let safeExtraHeight = Math.max(Number(extraHeight) || 0, 0)
	return Math.max(Math.ceil(textHeight + STICKY_NOTE_TEXT_PADDING_Y * 2 + STICKY_NOTE_HEIGHT_SAFETY_PX + safeExtraHeight), STICKY_NOTE_MIN_HEIGHT)
}

export function calculateStickyNoteTextHeight(text = "", width = STICKY_NOTE_DEFAULT_WIDTH) {
	let usableWidth = getStickyNoteTextWidth(width)
	let lines = countWrappedLines(text, usableWidth)
	return lines * getStickyNoteLineHeight()
}

export function calculateStickyNoteLineCount(text = "", width = STICKY_NOTE_DEFAULT_WIDTH) {
	let usableWidth = getStickyNoteTextWidth(width)
	return countWrappedLines(text, usableWidth)
}

export function getStickyNoteLineHeight() {
	return STICKY_NOTE_FONT_SIZE * STICKY_NOTE_LINE_HEIGHT
}

export function getStickyNoteTextWidth(width = STICKY_NOTE_DEFAULT_WIDTH) {
	let safeWidth = Math.max(Number(width) || STICKY_NOTE_DEFAULT_WIDTH, STICKY_NOTE_MIN_WIDTH)
	return Math.max(safeWidth - STICKY_NOTE_TEXT_PADDING_X * 2, STICKY_NOTE_FONT_SIZE)
}

function countWrappedLines(text, usableWidth) {
	let lines = 0
	let paragraphs = String(text || STICKY_NOTE_DEFAULT_TEXT).split("\n")
	let measureContext = getMeasureContext()
	for(let paragraph of paragraphs){
		let wrappedLines = wrapParagraph(paragraph, usableWidth, measureContext)
		lines += wrappedLines.length
	}
	return Math.max(lines, 1)
}

function wrapParagraph(paragraph, usableWidth, measureContext) {
	if(!paragraph){
		return [""]
	}

	let words = paragraph.split(/\s+/).filter(Boolean)
	if(words.length === 0){
		return [paragraph]
	}

	let lines = []
	let currentLine = ""
	for(let word of words){
		let candidateLine = currentLine ? `${currentLine} ${word}` : word
		if(measureTextWidth(candidateLine, measureContext) <= usableWidth){
			currentLine = candidateLine
			continue
		}
		if(currentLine){
			lines.push(currentLine)
			currentLine = ""
		}
		if(measureTextWidth(word, measureContext) <= usableWidth){
			currentLine = word
			continue
		}
		lines.push(...splitLongWord(word, usableWidth, measureContext))
	}

	if(currentLine){
		lines.push(currentLine)
	}

	return lines.length > 0 ? lines : [paragraph]
}

function splitLongWord(word, usableWidth, measureContext) {
	let lines = []
	let currentPart = ""
	for(let char of word){
		let candidatePart = currentPart + char
		if(currentPart && measureTextWidth(candidatePart, measureContext) > usableWidth){
			lines.push(currentPart)
			currentPart = char
			continue
		}
		currentPart = candidatePart
	}

	if(currentPart){
		lines.push(currentPart)
	}

	return lines
}

function measureTextWidth(text, measureContext) {
	return measureContext.measureText(text).width
}

function getMeasureContext() {
	if(stickyNoteMeasureContext){
		return stickyNoteMeasureContext
	}

	let canvas = document.createElement("canvas")
	stickyNoteMeasureContext = canvas.getContext("2d")
	stickyNoteMeasureContext.font = `${STICKY_NOTE_FONT_SIZE}px ${STICKY_NOTE_FONT_FAMILY}`
	return stickyNoteMeasureContext
}
